import React, { useState } from 'react';
import { Alert, Button, Divider, Input, Radio, Space, Spin, Switch, Tooltip, message } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import GalleryImagePicker from '../../blog/GalleryImagePicker';
import SerpPreview from './SerpPreview';
import OgCardPreview from './OgCardPreview';
import SeoChecklist from './SeoChecklist';
import {
  scoreSeoMeta,
  TITLE_MAX_LENGTH,
  DESCRIPTION_MAX_LENGTH,
} from './seoScore';
import { useSeoMeta } from './useSeoMeta';

const Container = styled.div`
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
  align-items: flex-start;

  .fields {
    flex: 10;
    min-width: 340px;
  }

  .previews {
    flex: 11;
    min-width: 400px;
  }

  .field {
    margin-bottom: 16px;
  }

  .field-label {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 4px;
  }

  .hint {
    font-weight: 400;
    color: #999;
  }

  .section-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #999;
    margin-bottom: 6px;
  }
`;

export interface SeoPanelProps {
  /** ContentType id of the record being described (from the table's OPTIONS response). */
  contentTypeId?: number;
  objectId?: number | string;
  /** Public path of the page, for the SERP preview — e.g. `/listing/casa-uno`. */
  path?: string;
  /** Falls back in the preview when no share image is set. */
  fallbackImageUrl?: string | null;
  /** Gallery to pick a share image from, matching the host model's picker. */
  galleryId?: number;
  onSaved?: () => void;
}

/**
 * Edits the `seo.SeoMeta` override for one content record — a listing, destination or
 * post — alongside a live Google result and shared-link preview.
 *
 * Every field is an override: left empty, the page keeps deriving that value from its
 * own content (the listing's short description, the post's excerpt, and so on). The
 * placeholders say so, because an empty box that silently means "inherit" is otherwise
 * indistinguishable from one that means "blank".
 *
 * Not driven by the generic `RecordDrawer`: this row is a *different* record from the one
 * the host form is editing, and `RecordFieldsMapper` drops `object_id` whenever
 * `content_type` is present, so the generic form cannot bind the row to its target. The
 * static-route table (`SeoPageModel`) does use the generic machinery, where that
 * constraint doesn't apply.
 */
const SeoPanel = ({
  contentTypeId,
  objectId,
  path,
  fallbackImageUrl,
  galleryId,
  onSaved,
}: SeoPanelProps) => {
  const {
    isLoading,
    isSaving,
    seoMeta,
    draft,
    localeCodes,
    setDraft,
    isDirty,
    save,
    error,
  } = useSeoMeta(contentTypeId, objectId);
  const [locale, setLocale] = useState<string | undefined>();
  const [imagePickerOpen, setImagePickerOpen] = useState(false);

  if (!contentTypeId || objectId === undefined) {
    return (
      <Alert
        type="info"
        showIcon
        message="Save this record first"
        description="SEO metadata attaches to a saved record."
      />
    );
  }

  if (isLoading) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  const activeLocale = locale && localeCodes.includes(locale) ? locale : localeCodes[0];
  const title = activeLocale ? draft.titles[activeLocale] || '' : '';
  const description = activeLocale ? draft.descriptions[activeLocale] || '' : '';

  const result = scoreSeoMeta({
    title,
    description,
    ogImage: draft.og_image,
    focusKeyword: draft.focus_keyword,
    noindex: draft.noindex,
  });

  const setLocalized = (
    key: 'titles' | 'descriptions',
    value: string,
  ) => {
    if (!activeLocale) return;
    setDraft(previous => ({
      ...previous,
      [key]: { ...previous[key], [activeLocale]: value },
    }));
  };

  const handleSave = async () => {
    try {
      await save();
      message.success('SEO metadata saved. The public page picks it up within a minute.');
      onSaved?.();
    } catch {
      // `error` is rendered below; the hook already captured the detail.
    }
  };

  return (
    <Container>
      <div className="fields">
        {error && (
          <Alert type="error" showIcon message="Could not save" description={error} style={{ marginBottom: 16 }} />
        )}

        {localeCodes.length > 1 && (
          <Radio.Group
            size="small"
            value={activeLocale}
            onChange={event => setLocale(event.target.value)}
            style={{ marginBottom: 16 }}
          >
            {localeCodes.map(code => (
              <Radio.Button key={code} value={code}>
                {code.toUpperCase()}
              </Radio.Button>
            ))}
          </Radio.Group>
        )}

        <div className="field">
          <div className="field-label">
            <span>Title</span>
            <span className="hint">
              {title.length}/{TITLE_MAX_LENGTH}
            </span>
          </div>
          <Input
            value={title}
            maxLength={TITLE_MAX_LENGTH}
            placeholder="Leave empty to use the page's own generated title"
            onChange={event => setLocalized('titles', event.target.value)}
          />
        </div>

        <div className="field">
          <div className="field-label">
            <span>Description</span>
            <span className="hint">
              {description.length}/{DESCRIPTION_MAX_LENGTH}
            </span>
          </div>
          <Input.TextArea
            value={description}
            maxLength={DESCRIPTION_MAX_LENGTH}
            autoSize={{ minRows: 3, maxRows: 6 }}
            placeholder="Leave empty to use the record's own description"
            onChange={event => setLocalized('descriptions', event.target.value)}
          />
        </div>

        <div className="field">
          <div className="field-label">
            <span>
              Focus keyword{' '}
              <Tooltip title="The search query this page should win. Used for scoring and for the SEO dashboard — never rendered on the page.">
                <QuestionCircleOutlined style={{ color: '#bbb' }} />
              </Tooltip>
            </span>
          </div>
          <Input
            value={draft.focus_keyword}
            placeholder="e.g. co-ownership homes punta mita"
            onChange={event =>
              setDraft(previous => ({ ...previous, focus_keyword: event.target.value }))
            }
          />
        </div>

        <div className="field">
          <div className="field-label">
            <span>Share image</span>
            <span className="hint">1200×630 works best</span>
          </div>
          {/*
            ImageUrlPicker can't be reused here — it binds itself to an antd Form via
            Form.useFormInstance, and this panel is plain controlled state. The
            underlying gallery modal is form-free, so it drives the same picker UX.
          */}
          <Input
            value={draft.og_image}
            placeholder="Leave empty to use the page's cover image"
            onChange={event =>
              setDraft(previous => ({ ...previous, og_image: event.target.value }))
            }
            addonAfter={
              <Button type="text" size="small" onClick={() => setImagePickerOpen(true)}>
                Select
              </Button>
            }
          />
          <GalleryImagePicker
            visible={imagePickerOpen}
            galleryId={galleryId}
            selectSize="fhd"
            onSelect={url => {
              setDraft(previous => ({ ...previous, og_image: url }));
              setImagePickerOpen(false);
            }}
            onCancel={() => setImagePickerOpen(false)}
          />
        </div>

        <Divider style={{ margin: '8px 0 16px' }} />

        <Space size={8} align="center" style={{ marginBottom: 16 }}>
          <Switch
            checked={draft.noindex}
            onChange={checked => setDraft(previous => ({ ...previous, noindex: checked }))}
          />
          <span>
            Hide from search engines
            <Tooltip title="Adds a noindex tag. The page stays reachable by direct link.">
              {' '}
              <QuestionCircleOutlined style={{ color: '#bbb' }} />
            </Tooltip>
          </span>
        </Space>

        <div className="field">
          <div className="field-label">
            <span>
              Canonical path{' '}
              <Tooltip title="Only set this to tell search engines another URL is the real version of this page. Almost always leave it empty.">
                <QuestionCircleOutlined style={{ color: '#bbb' }} />
              </Tooltip>
            </span>
          </div>
          <Input
            value={draft.canonical_path}
            placeholder="Leave empty — this page is its own canonical"
            onChange={event =>
              setDraft(previous => ({ ...previous, canonical_path: event.target.value }))
            }
          />
        </div>

        <Button type="primary" loading={isSaving} disabled={!isDirty} onClick={handleSave}>
          {seoMeta ? 'Save SEO metadata' : 'Create SEO metadata'}
        </Button>
        {!isDirty && seoMeta && (
          <span style={{ color: '#999', marginLeft: 12, fontSize: 12 }}>No changes</span>
        )}
      </div>

      <div className="previews">
        <SeoChecklist result={result} />

        <Divider style={{ margin: '18px 0 14px' }} />

        <div className="section-label">Google result</div>
        <SerpPreview
          title={title}
          description={description}
          path={path}
          locale={activeLocale}
        />

        <div className="section-label" style={{ marginTop: 18 }}>
          Shared link
        </div>
        <OgCardPreview
          title={title}
          description={description}
          imageUrl={draft.og_image || fallbackImageUrl}
        />
      </div>
    </Container>
  );
};

export default SeoPanel;
